import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import MainTableV2 from './MainTableV2';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import MobileHeader from './MobileHeader';
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPaperPlane, faUsers } from '@fortawesome/free-solid-svg-icons';
import ToggleSwitch from './ToggleSwitch';

const UserUserTable = () => {
  const { theme } = useTheme();
  const primaryColor = theme.primary;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  const clearAlert = () => setAlertMessage(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/dataServer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'getUsers',
            payload: { userId: currentUser?.userId }
          })
        });

        if (response.ok) {
          const fetchedData = await response.json();
          // console.log('fetched users: ', fetchedData);
          setData(fetchedData || []);
        } else {
          setAlertMessage(`Server Error: ${await response.text()}`);
        }
      } catch (error) {
        setAlertMessage(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, apiUrl]);

  const handleEditClick = (row) => {
    navigate(`/EditUserForm/${row.RecordId}`, { state: { rowData: row } });
  };

  const handleToggleDisable = async (userId, isDisabled, tenantId) => {
    setLoading(true);
    try {
      const response = await fetch('https://fuellink-db-ui.azurewebsites.net/api/disableUser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, isDisabled, tenantId })
      });

      if (response.ok) {
        setData(prevData =>
          prevData.map(user =>
            user.UserId === userId ? { ...user, isActive: !isDisabled } : user
          )
        );
        setAlertMessage(`Successfully ${isDisabled ? 'disabled' : 'enabled'} user`);
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Open the confirmation modal with a specific action
  const openModal = (action, row) => {
    setModalAction(action);
    setSelectedUser(row);
    setShowModal(true);
  };

  // Handle the action once the user confirms in the modal
  const confirmModalAction = async () => {
    if (!selectedUser) return;
    setShowModal(false);
    setLoading(true);

    try {
      const requestBody = {
        action: modalAction === 'delete' ? 'deleteUser' : 'resendInvite',
        payload: {
          targetUserId: selectedUser.UserId,
          requestingUserId: currentUser.userId,
          invitationToken: selectedUser.InvitationToken
        },
      };

      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();
      // console.log('Response: ', responseData);

      if (!responseData.success) {
        setAlertMessage(`${responseData.message}`);
        setLoading(false);
        return;
      }

      if (modalAction === 'delete') {
        // Remove the deleted user from the local state
        setData(prevData => prevData.filter(u => u.UserId !== selectedUser.UserId));
        setAlertMessage('Successfully deleted user.');
      } else {
        setData(prevData => 
          prevData.map(user => 
            user.InvitationToken === selectedUser.InvitationToken ? { ...user, InvitationToken: responseData.invitationToken } : user
          )
        );
        // console.log(data);
        setAlertMessage('Successfully resent invitation.');
      }
    } catch (error) {      
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    if (searchQuery === '') return data;
    return data.filter(row =>
      Object.values(row).some(value =>
        value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, data]);

  const columns = useMemo(() => [
    {
      name: 'First Name',
      selector: row => row.FirstName,
      sortable: true,
      reorder: true
    },
    {
      name: 'Last Name',
      selector: row => row.LastName,
      sortable: true,
      reorder: true
    },
    {
      name: 'Email',
      selector: row => row.Email,
      sortable: true,
      reorder: true
    },
    {
      name: 'Phone',
      selector: row => row.Phone,
      sortable: true,
      reorder: true
    },
    {
      name: 'Disabled',
      cell: row => (
        row.UserId === 'Pending' ? (
          <span className="text-gray-500 italic">Invite Sent</span>
        ) : row.UserId !== currentUser.userId && (
          <ToggleSwitch
            isChecked={!row.isActive}
            onToggle={() => handleToggleDisable(row.UserId, row.isActive, row.TenantId)}
            primaryColor={theme.primary}
          />
        )
      ),
      ignoreRowClick: true
    },    
    {
      name: 'Actions',
      cell: row => (
        <div className="flex space-x-3">
          {/* Edit */}
          <FontAwesomeIcon
            icon={faEdit}
            className="themedIcon2xl"
            title="Edit User"
            onClick={() => handleEditClick(row)}
          />

          {/* Resend Invite */}
          <FontAwesomeIcon
            icon={faPaperPlane}
            className={`themedIcon2xl ${row.UserId !== 'Pending' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            title='Resend Invite'
            onClick={() => {
              if (row.UserId === 'Pending') {
                openModal('resend', row);
              }
            }}
          />        
  
          {/* Delete */}
          <FontAwesomeIcon
            icon={faTrash}
            className={`themedIcon2xl ${row.UserId === currentUser.userId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            title={row.UserId === currentUser.userId ? 'You cannot delete yourself' : 'Delete User'}
            onClick={() => {
              if (row.UserId !== currentUser.userId) {
                openModal('delete', row);
              }
            }}
          />
        </div>
      ),
      ignoreRowClick: true
    }
  ], [handleEditClick, theme.primary, currentUser.userId]);

  const filterConfig = [
    { key: 'FirstName', type: 'string' },
    { key: 'LastName', type: 'string' },
    { key: 'Email', type: 'string' },
    { key: 'Phone', type: 'string' },
  ];

  return (
    <>
      <MobileHeader theme={theme} />
      <div className='mobile-container'>
        <PageHeader
          heading="User Management"
          context="Manage user accounts"
          icon={<FontAwesomeIcon icon={faUsers} />}
        />

        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Spinner />
          </div>
        ) : (
          <MainTableV2
            columns={columns}
            data={filteredData}
            primaryColor={primaryColor}
            loading={loading}
            alertMessage={alertMessage}
            clearAlert={clearAlert}            
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            filterConfig={filterConfig}
          >
            <button
              className="themed-button"
              onClick={() => navigate('/adduser')}
            >
              Add New User
            </button>
          </MainTableV2>
        )}

        {/* Confirmation Modal */}
        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h2 className="text-xl font-bold mb-4">
                {modalAction === 'delete' ? 'Delete User?' : 'Resend Invitation?'}
              </h2>
              <p className="mb-4">
                {modalAction === 'delete'
                  ? `Are you sure you want to delete "${selectedUser?.Email}"? This action can't be undone.`
                  : `Resend the invite to "${selectedUser?.Email}"?`}
              </p>
              <div className="flex justify-end space-x-2">
                <button
                  className="px-4 py-2 bg-gray-300 rounded"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="themed-button"
                  onClick={confirmModalAction}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserUserTable;